import {Tag, Tags, TagType} from '@wandb/weave/common/components/Tags';
import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {useContext, useMemo} from 'react';
import {CellInfo, Column} from 'react-table';

import {
  RegistriesSearchContext,
  SingleRegistrySearchContext,
} from '../../../components/Registries/common/RegistriesSearchContextProvider';
import {CardCollectionType} from '../../../components/Registries/common/types';
import {RawTag} from '../../TagAssignment';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {makeColumnSortMethod} from '../../WBReactTable';
import {ArtifactMembershipActions} from './ArtifactMembershipActions';
import * as S from './PanelArtifactHistoryQuickView.styles';
import {Table} from './Table';
import {sortAliases} from './utils';

const INFO_COLUMN_MAP = new Map<string, string>([
  ['created', 'created'],
  ['consumingRuns', '# of consuming runs'],
  ['size', 'size'],
]);

export type MembershipRow = {
  versionIndex: number;
  versionId: string;
  aliases: string[];
  tags: RawTag[];
  metadata: Record<string, any>;
  created: string;
  consumingRuns: number;
  size: string;
};

export type ArtifactMembershipsActionsData = {
  isPortfolio: boolean;
  collectionId: string;
  collectionName: string;
  projectName: string;
  entityName: string;
  hasMemberships: boolean;
};

type ExtraTableProps = {
  pageSize: number;
  onChangePageSize: (pageSize: number) => void;
  hasNextPage: boolean;
  fetchingNextPage: boolean;
  onFetchNextPage: () => void;
};

type ArtifactMembershipsTableProps = {
  artifactMembershipsData: MembershipRow[];
  onRowClick: (versionIndex: number) => void;
  border?: string;
  tableProps?: ExtraTableProps;
  chosenMetadataKeys: string[];
  loading: boolean;
  artifactMembershipsActionsData: ArtifactMembershipsActionsData;
  isArtifactMatch?: boolean;
  cardCollectionType: CardCollectionType;
  protectedAliases?: string[];
};

export const ArtifactMembershipsTable = ({
  artifactMembershipsData,
  onRowClick,
  border,
  tableProps,
  chosenMetadataKeys,
  loading,
  artifactMembershipsActionsData,
  isArtifactMatch,
  cardCollectionType,
  protectedAliases,
}: ArtifactMembershipsTableProps) => {
  const searchContext = useMemo(() => {
    return cardCollectionType === CardCollectionType.SINGLE_REGISTRY
      ? SingleRegistrySearchContext
      : RegistriesSearchContext;
  }, [cardCollectionType]);
  const {searchFilter} = useContext(searchContext);

  const rows = useMemo(() => {
    return artifactMembershipsData?.map((membership: MembershipRow) => {
      const id = membership;
      const aliases = membership.aliases?.map(alias => {
        const isProtectedAlias = protectedAliases?.includes(alias);
        return {
          name: alias,
          isProtectedAlias,
        };
      });
      const tags = membership.tags?.map(tag => {
        return {
          name: tag.name,
          colorIndex: TagType.TAG, // TODO: Support different color per tag (see RFC specs)
        };
      });
      const {versionIndex, metadata, created, consumingRuns, size} = membership;
      return {
        searchString: '',
        row: {
          id,
          versionIndex,
          aliases,
          tags,
          metadata,
          created,
          consumingRuns,
          size,
        },
      };
    });
  }, [artifactMembershipsData, protectedAliases]);

  const columns: Column[] = [
    {
      Header: 'VERSIONS',
      id: 'versionIndex',
      accessor: 'versionIndex',
      resizable: true,
      width: 100,
      Cell: (cellInfo: CellInfo) => {
        return (
          <S.Link>
            <S.Text onClick={() => onRowClick(cellInfo.value)}>
              V{cellInfo.value}
            </S.Text>
          </S.Link>
        );
      },
    },
    {
      Header: 'ALIASES',
      accessor: 'aliases',
      resizable: true,
      sortable: false,
      width: 250,
      Cell: (cellInfo: CellInfo) => {
        return (
          <S.AliasOrTagList>
            <div className="run-tags">
              {cellInfo.value.sort(sortAliases).map((alias: any) => (
                <Tag
                  key={alias.name}
                  tag={{
                    name: alias.name,
                    colorIndex: alias.isProtectedAlias
                      ? TagType.PROTECTED_ALIAS
                      : TagType.ALIAS,
                  }}
                  noun={alias.isProtectedAlias ? 'protected-alias' : 'alias'}
                  highlightedText={isArtifactMatch ? searchFilter : undefined}
                />
              ))}
            </div>
          </S.AliasOrTagList>
        );
      },
    },
    {
      Header: 'TAGS',
      accessor: 'tags',
      resizable: true,
      sortable: false,
      width: 250,
      Cell: (cellInfo: CellInfo) => {
        return (
          <S.AliasOrTagList>
            <Tags
              tags={cellInfo.value}
              noun="tag"
              highlightedText={isArtifactMatch ? searchFilter : undefined}
            />
          </S.AliasOrTagList>
        );
      },
    },
  ];

  chosenMetadataKeys.forEach(metadataKey => {
    columns.push({
      // Since all the metadata columns have the same accessor,
      // they need an id to differentiate them for sorting to work
      Header: metadataKey.toUpperCase(),
      id: metadataKey,
      accessor: 'metadata',
      resizable: true,
      width: 180,
      sortMethod: makeColumnSortMethod(metadata => metadata[metadataKey]),
      Cell: (cellInfo: CellInfo) => {
        // eslint-disable-next-line no-prototype-builtins
        const value = cellInfo.value?.hasOwnProperty(metadataKey)
          ? cellInfo.value[metadataKey]
          : null;
        return <div>{value}</div>;
      },
    });
  });

  // In some cases, the versions neither have metadata nor history metrics.
  // Then, we show some user information
  let count = chosenMetadataKeys.length;
  for (const columnKey of INFO_COLUMN_MAP.keys()) {
    if (count >= 4) {
      break;
    }
    columns.push({
      Header: INFO_COLUMN_MAP.get(columnKey)?.toUpperCase(),
      accessor: columnKey,
      resizable: true,
      width: 180,
      Cell: (cellInfo: CellInfo) => {
        /* eslint-disable no-case-declarations */
        // FIXME: There are a few variables assigned in `case` statements that
        //        should be reworked. Ignore the issue here rather than fix and
        //        potentially introduce new bugs.
        switch (columnKey) {
          case 'loggedBy':
            const runKey = 'name';
            const linkKey = 'url';
            // eslint-disable-next-line no-prototype-builtins
            const value = cellInfo.value?.hasOwnProperty(runKey)
              ? cellInfo.value[runKey]
              : null;
            // eslint-disable-next-line no-prototype-builtins
            return cellInfo.value?.hasOwnProperty(linkKey) ? (
              <S.Link>
                <TargetBlank href={cellInfo.value[linkKey]}>
                  {value}
                </TargetBlank>
              </S.Link>
            ) : (
              <div>{value}</div>
            );
          case 'created':
            if (!cellInfo.value) {
              return null;
            }
            const date = new Date(cellInfo.value);
            return <div>{date.toDateString()}</div>;
          default:
            return <div>{cellInfo.value}</div>;
        }
      },
    });
    count += 1;
  }

  columns.push({
    Header: '',
    accessor: 'id',
    resizable: false,
    sortable: false,
    Cell: (cellInfo: CellInfo) => {
      return (
        <ArtifactMembershipActions
          isPortfolio={artifactMembershipsActionsData.isPortfolio}
          versionIndex={cellInfo.value.versionIndex}
          versionId={cellInfo.value.versionId}
          collectionId={artifactMembershipsActionsData.collectionId}
          collectionName={artifactMembershipsActionsData.collectionName}
          projectName={artifactMembershipsActionsData.projectName}
          entityName={artifactMembershipsActionsData.entityName}
          hasAliases={cellInfo.value.aliases.length > 0}
          hasMemberships={artifactMembershipsData.length > 0}
          onViewDetails={onRowClick}
        />
      );
    },
  });
  if (loading) {
    return (
      <div style={{padding: '4px'}}>
        <Loader
          name="artifact-versions-table-quick-view"
          active
          inline="centered"
          size="small"
        />
      </div>
    );
  }
  return (
    <Table
      noSearch={true}
      data={rows}
      columns={columns}
      pageSize={5}
      border={border}
      {...tableProps}
    />
  );
};
